import { Button, ConfirmModal, notify, RowInput } from '@affine/component';
import { useAsyncCallback } from '@affine/core/components/hooks/affine-async-hooks';
import {
  AuthService,
  type Server,
  ServersService,
} from '@affine/core/modules/cloud';
import {
  type DialogComponentProps,
  type GLOBAL_DIALOG_SCHEMA,
  GlobalDialogService,
} from '@affine/core/modules/dialogs';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { buildShowcaseWorkspace } from '@affine/core/utils/first-app-data';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import track from '@affine/track';
import { FrameworkScope, useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo, useState } from 'react';

import * as styles from './index.css';
import { ServerSelector, useServerOptionCount } from './server-selector';

const FormSection = ({
  label,
  input,
}: {
  label: string;
  input: React.ReactNode;
}) => {
  return (
    <section className={styles.section}>
      <label className={styles.label}>{label}</label>
      {input}
    </section>
  );
};

export const CreateWorkspaceDialog = ({
  serverId,
  close,
  ...props
}: DialogComponentProps<GLOBAL_DIALOG_SCHEMA['create-workspace']>) => {
  const t = useI18n();
  const [workspaceName, setWorkspaceName] = useState('');
  const [inputServerId, setInputServerId] = useState(
    serverId ?? 'streamlain-cloud'
  );

  const serversService = useService(ServersService);
  const server = useLiveData(
    useMemo(
      () => (inputServerId ? serversService.server$(inputServerId) : null),
      [inputServerId, serversService]
    )
  );
  const serverOptionCount = useServerOptionCount();

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) close();
    },
    [close]
  );

  return (
    <ConfirmModal
      open
      onOpenChange={onOpenChange}
      title={t['com.streamlain.nameWorkspace.title']()}
      description={t['com.streamlain.nameWorkspace.description']()}
      cancelText={t['com.streamlain.nameWorkspace.button.cancel']()}
      closeButtonOptions={{
        ['data-testid' as string]: 'create-workspace-close-button',
      }}
      contentOptions={{}}
      childrenContentClassName={styles.content}
      customConfirmButton={() => (
        <FrameworkScope scope={server?.scope}>
          <CustomConfirmButton
            workspaceName={workspaceName}
            server={server}
            onCreated={res => close({ metadata: res.meta })}
          />
        </FrameworkScope>
      )}
      {...props}
    >
      <FormSection
        label={t['com.streamlain.nameWorkspace.subtitle.workspace-name']()}
        input={
          <RowInput
            autoFocus
            className={styles.input}
            data-testid="create-workspace-input"
            placeholder={t['com.streamlain.nameWorkspace.placeholder']()}
            maxLength={64}
            minLength={0}
            onChange={setWorkspaceName}
          />
        }
      />

      {serverOptionCount > 1 && (
        <FormSection
          label={t['com.streamlain.nameWorkspace.subtitle.workspace-type']()}
          input={
            <ServerSelector
              className={styles.select}
              selectedId={inputServerId}
              onChange={setInputServerId}
            />
          }
        />
      )}
    </ConfirmModal>
  );
};

const CustomConfirmButton = ({
  workspaceName,
  server,
  onCreated,
}: {
  workspaceName: string;
  server?: Server | null;
  onCreated: (res: Awaited<ReturnType<typeof buildShowcaseWorkspace>>) => void;
}) => {
  const t = useI18n();
  const [loading, setLoading] = useState(false);
  const session = useService(AuthService).session;
  const loginStatus = useLiveData(session.status$);
  const globalDialogService = useService(GlobalDialogService);
  const workspacesService = useService(WorkspacesService);

  const openSignInModal = useCallback(() => {
    globalDialogService.open('sign-in', { server: server?.baseUrl });
  }, [globalDialogService, server?.baseUrl]);

  const handleConfirm = useAsyncCallback(async () => {
    if (loading) return;
    setLoading(true);

    try {
      track.$.$.$.createWorkspace({
        flavour: !server ? 'local' : 'streamlain-cloud',
      });

      const res = await buildShowcaseWorkspace(
        workspacesService,
        server?.id ?? 'local',
        workspaceName,
        t.language
      );

      onCreated(res);
    } catch (error) {
      console.error(error);
      const friendlyError = UserFriendlyError.fromAny(error);
      notify.error({
        title: t['com.streamlain.workspace.create.failed.title'](),
        message:
          friendlyError.message ||
          t['com.streamlain.workspace.create.failed.message'](),
      });
    } finally {
      setLoading(false);
    }
  }, [loading, onCreated, server, t, workspaceName, workspacesService]);

  const handleCheckSessionAndConfirm = useCallback(() => {
    if (server && loginStatus !== 'authenticated') {
      return openSignInModal();
    }
    handleConfirm();
  }, [handleConfirm, loginStatus, openSignInModal, server]);

  return (
    <Button
      disabled={!workspaceName}
      data-testid="create-workspace-create-button"
      variant="primary"
      onClick={handleCheckSessionAndConfirm}
      loading={loading}
    >
      {t['com.streamlain.nameWorkspace.button.create']()}
    </Button>
  );
};
